import { Info, Bell, Plus } from "lucide-react";
import { useState } from "react";
import { getEmojiImage } from "../Dashboards/utils";
import { settingsTimeOptions } from "./settingsTimeOptions";
import * as Slider from "@radix-ui/react-slider";
import { useEmotionSettings } from "@/hooks/useEmotionSettings";
import RuleConflictError from "./RuleConflictError";
import { useBootstrap } from "../../features/bootstrap";
import { ROLES } from "../../features/router/roles";

const emotionList = [
  {
    name: "sad",
    label: "Sad",
    img: "/images/emotions/sad.png",
    color: "#5b9cd6",
  },
  {
    name: "tired",
    label: "Tired",
    img: "/images/emotions/tired.png",
    color: "#5b9cd6",
  },
  {
    name: "lonely",
    label: "Lonely",
    img: "/images/emotions/lonely.png",
    color: "#5b9cd6",
  },
  {
    name: "happy",
    label: "Happy",
    img: "/images/emotions/happy.png",
    color: "#4cc68d",
  },
  {
    name: "grateful",
    label: "Grateful",
    img: "/images/emotions/grateful.png",
    color: "#4cc68d",
  },
  {
    name: "calm",
    label: "Calm",
    img: "/images/emotions/calm.png",
    color: "#4cc68d",
  },
  {
    name: "stressed",
    label: "Stressed",
    img: "/images/emotions/stressed.png",
    color: "#bd92e4",
  },
  {
    name: "scared",
    label: "Scared",
    img: "/images/emotions/scared.png",
    color: "#bd92e4",
  },
  {
    name: "angry",
    label: "Angry",
    img: "/images/emotions/angry.png",
    color: "#bd92e4",
  },
];

const priorityOptions = [
  {
    value: 2,
    title: "Tier 2 (Yellow flag)",
    description: "- Moderate concern, monitor situation",
  },
  {
    value: 3,
    title: "Tier 3 (Red flag)",
    description: "- High priority, immediate intervention needed",
  },
];

export default function AddEmotionAlert({ show, onClose }) {
  const { data: user } = useBootstrap();
  const [selectedEmotion, setSelectedEmotion] = useState("sad");
  const [thresholdType, setThresholdType] = useState("percentage");
  const [percentageThreshold, setPercentageThreshold] = useState(25);
  const [countThreshold, setCountThreshold] = useState(4);
  const [selectedTime, setSelectedTime] = useState(30);
  const [visibility, setVisibility] = useState("");
  const [priority, setPriority] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [currentViolationIndex, setCurrentViolationIndex] = useState(0);

  const emotionColor =
    emotionList.find((e) => e.name === selectedEmotion)?.color ?? "#4579A6";

  const { createEmotionSettingsMutation } = useEmotionSettings();

  const {
    mutateAsync: createEmotionSettingMutate,
    isPending: createEmotionSettingMutateIsPending,
    error: createEmotionSettingMutateError,
  } = createEmotionSettingsMutation;

  const violations = createEmotionSettingMutateError?.violations || [];

  const sortedViolations = violations.sort((a, b) => {
    const scopeOrder = ["default", "district", "school", "custom"];
    const aScope = a.violation.existingRuleType;
    const bScope = b.violation.existingRuleType;
    return scopeOrder.indexOf(aScope) - scopeOrder.indexOf(bScope);
  });

  const handleSaveAlert = async () => {
    setErrorMsg("");
    setShowErrorModal(false);
    setCurrentViolationIndex(0);
    if (!selectedEmotion) {
      setErrorMsg("Please select an emotion.");
      return;
    }
    if (!priority) {
      setErrorMsg("Please select a priority.");
      return;
    }
    if (
      !percentageThreshold ||
      percentageThreshold < 1 ||
      percentageThreshold > 100
    ) {
      setErrorMsg("Please set a valid percentage threshold (1-100).");
      return;
    }
    if (!countThreshold || countThreshold < 1) {
      setErrorMsg("Please set a valid count threshold (at least 1).");
      return;
    }
    if (!selectedTime) {
      setErrorMsg("Please select a time window.");
      return;
    }

    let scope = "custom";
    let entityId = user?._id;
    if (user?.role === "123wellness") {
      scope = "default";
      entityId = null;
    } else if (user?.role === "superadmin") {
      if (visibility === "district") {
        scope = "district";
        entityId = user?.districts[0]?._id;
      } else {
        scope = "custom";
        entityId = user?._id;
      }
    } else if (user.role === "admin") {
      if (visibility === "school") {
        scope = "school";
        entityId = user?.schools[0]?._id;
      } else {
        scope = "custom";
        entityId = user?._id;
      }
    } else if (user?.role === "teacher") {
      scope = "custom";
      entityId = user?._id;
    }

    const tier = priority;

    const payload = {
      emotion: selectedEmotion,
      threshold_percentage: percentageThreshold,
      threshold_count: countThreshold,
      days: selectedTime,
      tier,
      scope,
      entityId,
      createdBy: user._id.toString(),
    };

    try {
      await createEmotionSettingMutate([payload]);
      onClose();
    } catch (err) {
      if (err?.violations?.length > 0) {
        setShowErrorModal(true);
      } else {
        setErrorMsg(err?.message || "Failed to save alert. Please try again.");
      }
    }
  };

  const handleNextViolation = () => {
    if (currentViolationIndex < sortedViolations.length - 1) {
      setCurrentViolationIndex(currentViolationIndex + 1);
    }
  };

  const handlePreviousViolation = () => {
    if (currentViolationIndex > 0) {
      setCurrentViolationIndex(currentViolationIndex - 1);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-10 flex flex-column items-center justify-center">
      <div className="absolute inset-0 bg-black/30"></div>
      <div className="bg-white rounded sm:ml-[130px] w-[90%] sm:w-[50%] xl:w-[40%] 2xl:w-[30%] shadow relative z-10">
        <div className="text-[18px] flex py-2 px-4 border-b border-b-[#DDDDDD] sticky z-10 top-0 bg-white">
          ADD EMOTION ALERT
        </div>
        <div className="py-3 px-4 flex flex-col gap-2 overflow-y-auto sm:max-h-[80vh] max-h-[70vh]">
          <div className="flex flex-col items-center gap-1 bg-[#FBFDFD] border border-[#DDDDDD] p-3 rounded">
            <div className="flex flex-row items-center gap-2">
              <Info className="w-4 h-4 opacity-50" />
              <div className="font-semibold text-[14px]">
                How Emotion Alerts Work
              </div>
            </div>
            <p className="text-[14px]">
              Monitor student emotional patterns and receive notifications when
              specific thresholds are met. This helps identify when students are
              experiencing different emotions.
            </p>
          </div>

          <div>
            <div className="text[16px] font-semibold mb-2 block">Emotion</div>
            <div className="relative w-full">
              <button
                onClick={() => setShowDropdown((prev) => !prev)}
                className="border p-1.5 rounded flex items-center justify-between sm:w-[30%] w-[40%]"
              >
                {selectedEmotion ? (
                  <div className="flex items-center gap-2">
                    <img
                      src={getEmojiImage(selectedEmotion)}
                      alt={selectedEmotion}
                      className="w-5 h-5"
                    />
                    <span className="capitalize">{selectedEmotion}</span>
                  </div>
                ) : (
                  <span className="text-gray-400">Select an emotion</span>
                )}
                <svg
                  className="w-3 h-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {showDropdown && (
                <div className="absolute sm:w-[30%] w-[40%] z-10 mt-1 bg-white border rounded shadow p-2">
                  {emotionList.map(({ name, label, img }) => (
                    <div
                      key={name}
                      className="flex items-center gap-2 p-1.5 hover:bg-gray-100 cursor-pointer capitalize"
                      onClick={() => {
                        setSelectedEmotion(name);
                        setShowDropdown(false);
                      }}
                    >
                      <img src={img} alt={label} className="w-5 h-5" />
                      <span>{label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex-col text-[16px]">
            <div className="font-semibold mb-2 block">Threshold</div>
            <div className="mb-2">
              <button
                className={`px-3 py-0.9 rounded-2xl font-semibold mb-1 ${
                  thresholdType === "percentage"
                    ? "text-white"
                    : "border border-[#DDDDDD] bg-white text-black"
                }`}
                style={
                  thresholdType === "percentage"
                    ? { backgroundColor: emotionColor }
                    : {}
                }
                onClick={() => setThresholdType("percentage")}
              >
                Percentage
              </button>
              <div className="flex flex-row items-center gap-3">
                <Slider.Root
                  className="relative flex items-center select-none touch-none w-full h-3"
                  value={[percentageThreshold]}
                  max={100}
                  step={1}
                  onValueChange={([val]) => setPercentageThreshold(val)}
                >
                  <Slider.Track className="bg-white relative border border-[#BBBBBB] grow rounded-full h-2.5">
                    <Slider.Range
                      className="absolute rounded-full h-full border-1 opacity-75"
                      style={{
                        backgroundColor: emotionColor,
                        borderColor: emotionColor,
                      }}
                    />
                  </Slider.Track>
                  <Slider.Thumb
                    className="block w-5 h-5 rounded-full opacity-75"
                    style={{ backgroundColor: emotionColor }}
                  />
                </Slider.Root>
                <div className="text-[16px] rounded p-1 border border-[#DDDDDD]">
                  {percentageThreshold}%
                </div>
              </div>
            </div>

            <div className="flex flex-row items-center gap-10 text-[16px]">
              <div>
                <div className="font-medium mb-1.5">Within</div>
                <select
                  className="border border-[#DDDDDD] bg-white rounded px-2 py-1 text-[16px]"
                  value={selectedTime}
                  onChange={(e) => setSelectedTime(Number(e.target.value))}
                >
                  {settingsTimeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.display}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <div className="mb-1.5 font-medium">Count</div>
                <div className="flex flex-row items-center gap-2">
                  <input
                    type="number"
                    min={1}
                    max={100}
                    value={countThreshold}
                    onChange={(e) => {
                      const val = Number(e.target.value);
                      if (val >= 0 && val <= 100) {
                        setCountThreshold(val);
                      }
                    }}
                    className="w-16 px-2 py-0.5 border border-[#DDDDDD] rounded cursor-pointer"
                  />
                  <span className="text-[16px]">times</span>
                </div>
              </div>
            </div>
          </div>

          <div
            className="border-1 rounded px-3 py-2 mt-1 bg-[#FBFDFD]"
            style={{ borderColor: emotionColor }}
          >
            <div className="flex items-center gap-2 mb-1 text-[14px] font-semibold">
              <Bell className="w-4 h-4" />
              Alert Me When:
            </div>
            <div className="text-[14px]">
              A student reports feeling{" "}
              <span className="font-semibold" style={{ color: emotionColor }}>
                {selectedEmotion}
              </span>
              {""} over
              <span className="font-semibold" style={{ color: emotionColor }}>
                {" "}
                {percentageThreshold}%{" "}
              </span>
              of the time in the past {""}
              <span className="font-semibold" style={{ color: emotionColor }}>
                {settingsTimeOptions.find(
                  (option) => option.value === selectedTime,
                )?.display || selectedTime}
              </span>
              <div className="mt-1 italic">
                Only alert me if they've practiced wellness at least
                <span className="font-semibold" style={{ color: emotionColor }}>
                  {" "}
                  {countThreshold}{" "}
                </span>
                {countThreshold === 1 ? "time" : "times"} in this time-period.
              </div>
            </div>
          </div>

          <div>
            <div className="text-[16px] font-semibold mb-2">Visibility</div>
            <div className="flex flex-col gap-2 text-[14px]">
              {user?.role === ROLES.TECH ? (
                <div className="flex items-center gap-2">
                  <input type="radio" checked readOnly />
                  <span className="font-semibold">Default Rule </span> - This
                  alert will be visible to all users
                </div>
              ) : user?.role === ROLES.TEACHER ? (
                <div className="flex items-center gap-2">
                  <input type="radio" checked readOnly />
                  <span className="font-semibold">Custom Rule </span> - Only you
                  will see this alert
                </div>
              ) : (
                <>
                  {user?.role === ROLES.SUPERADMIN && (
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="visibility"
                        value="district"
                        checked={visibility === "district"}
                        onChange={() => setVisibility("district")}
                      />
                      <span className="font-semibold">District-wide Rule</span>{" "}
                      - All schools in your district will see this alert
                    </div>
                  )}
                  {user.role === ROLES.ADMIN && (
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="visibility"
                        value="school"
                        checked={visibility === "school"}
                        onChange={() => setVisibility("school")}
                      />
                      <span className="font-semibold">School-wide Rule</span> -
                      All teachers in your school will see this alert
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="visibility"
                      value="custom"
                      checked={visibility === "custom"}
                      onChange={() => setVisibility("custom")}
                    />
                    <span className="font-semibold">Custom Rule</span> - Only
                    you will see this alert
                  </div>
                </>
              )}
            </div>
          </div>

          <div>
            <div className="text-[16px] font-semibold mb-2">Priority</div>
            <div className="flex flex-col gap-2 text-[14px]">
              {priorityOptions.map((option) => (
                <div key={option.value} className="flex gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="tier"
                    value={option.value}
                    checked={priority === option.value}
                    onChange={() => setPriority(option.value)}
                  />
                  <span className="font-semibold">{option.title}</span>
                  <span>{option.description}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        {errorMsg && (
          <div className="text-red-600 text-[15px] px-4 pb-2">{errorMsg}</div>
        )}
        <button
          className="bg-[#156E45] text-white py-1 px-3 mb-4 mx-3 mt-2 sm:mt-0 rounded text-[16px] font-semibold hover:opacity-90 disabled:opacity-60"
          onClick={handleSaveAlert}
          disabled={createEmotionSettingMutateIsPending}
        >
          {createEmotionSettingMutateIsPending ? "Saving..." : "Save Alert"}
        </button>
        <button
          onClick={onClose}
          className="py-1 px-3 mt-2 sm:mt-0 rounded text-[16px] font-semibold transition-colors bg-gray-300 text-gray-700 hover:bg-gray-400"
        >
          Cancel
        </button>
      </div>
      {showErrorModal && sortedViolations.length > 0 && (
        <RuleConflictError
          {...sortedViolations[currentViolationIndex].violation}
          onClose={() => {
            setShowErrorModal(false);
            setCurrentViolationIndex(0);
          }}
        />
      )}
    </div>
  );
}
